import { districtProfiles } from '../config/localSeoGenerator';
import React from 'react';
import { Link } from 'react-router-dom';
import { contactConfig } from '../config/contactConfig';
import { PhoneIcon, EnvelopeIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { OPEN_CONSENT_EVENT } from './ConsentBanner';


const Footer = () => {
  return (
    <footer className="bg-[#111817] text-white" role="contentinfo">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4 lg:gap-12">
          {/* Company Info */}
          <div>
            <h3 className="sr-only">Olgun Kepenk</h3>
            <img src="/assets/img/olgun-kepenk-logo.svg" alt="Olgun Kepenk" width="210" height="64" loading="lazy" className="mb-5 h-20 w-auto" />
            <p className="text-white/80 mb-4">
              Profesyonel kepenk sistemleri ve güvenlik çözümleri ile işletmenizi koruyoruz.
            </p>
            <div className="space-y-2">
              <a 
                href={`tel:${contactConfig.phone}`}
                className="flex items-center text-white/80 hover:text-white transition-colors duration-200"
              >
                <PhoneIcon className="w-5 h-5 mr-2" />
                {contactConfig.phoneDisplay}
              </a>
              <a 
                href={`mailto:${contactConfig.email}`}
                className="flex items-center text-white/80 hover:text-white transition-colors duration-200"
              >
                <EnvelopeIcon className="w-5 h-5 mr-2" />
                {contactConfig.email}
              </a>
              <p className="flex items-center text-white/80">
                <MapPinIcon className="w-5 h-5 mr-2" />
                {contactConfig.serviceArea}
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-4 relative inline-block">
              Hızlı Bağlantılar
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-light transform scale-x-0 hover:scale-x-100 transition-transform duration-300"></span>
            </h3>
            <ul className="space-y-2">
              {[
                { to: '/', label: 'Anasayfa' },
                { to: '/hakkimizda', label: 'Hakkımızda' },
                { to: '/blog', label: 'Blog' },
                { to: '/bilgi-merkezi', label: 'Bilgi Merkezi' },
                { to: '/iletisim', label: 'İletişim' },
                { to: '/gizlilik', label: 'Gizlilik ve Çerezler' }
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to}
                    className="text-white/80 hover:text-white transition-colors duration-200"
                    aria-label={link.label}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-xl font-bold mb-4 relative inline-block">
              Hizmetlerimiz
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-light transform scale-x-0 hover:scale-x-100 transition-transform duration-300"></span>
            </h3>
            <ul className="space-y-2">
              {[
                { to: '/kepenk-servisi', label: 'Otomatik Kepenk Servisi' },
                { to: '/kepenk-tamiri', label: 'Otomatik Kepenk Tamiri' },
                { to: '/sifir-kepenk', label: 'Sıfır Kepenk' },
                { to: '/kepenk-parcalari', label: 'Kepenk Parçaları' }
              ].map((service) => (
                <li key={service.to}>
                  <Link 
                    to={service.to}
                    className="text-white/80 hover:text-white transition-colors duration-200"
                    aria-label={service.label}
                  >
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-xl font-bold mb-4 relative inline-block">
              Kaynaklar
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-light transform scale-x-0 hover:scale-x-100 transition-transform duration-300"></span>
            </h3>
            <ul className="space-y-2">
              {[
                { to: '/bilgi-merkezi/kategori/kullanim-kilavuzlari', label: 'Kullanım Kılavuzları' },
                { to: '/bilgi-merkezi/kategori/bakim-tavsiyeleri', label: 'Bakım Tavsiyeleri' },
                { to: '/bilgi-merkezi/kategori/teknik-dokumanlar', label: 'Teknik Dökümanlar' }
              ].map((resource) => (
                <li key={resource.to}>
                  <Link 
                    to={resource.to}
                    className="text-white/80 hover:text-white transition-colors duration-200"
                    aria-label={resource.label}
                  >
                    {resource.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 border-t border-white/15 pt-10">
          <h3 className="mb-5 text-lg font-semibold">İstanbul İlçe Kepenk Servisleri</h3>
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {districtProfiles.map((district) => (
              <Link
                key={district.slug}
                to={`/${district.slug}`}
                className="rounded-lg border border-white/10 px-3 py-2 text-center text-white/65 transition hover:border-[#d8ba86] hover:text-[#d8ba86]"
              >
                {district.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Social Media Links */}
        <div className="mt-12 border-t border-white/15 pt-8">
          <div className="flex justify-center space-x-6">
            {contactConfig.socialMedia.instagram && (
              <a
                href={contactConfig.socialMedia.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 hover:text-white transition-colors duration-200"
                aria-label="Instagram"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                </svg>
              </a>
            )}
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-8 text-center text-white/50">
          <p>&copy; {new Date().getFullYear()} Olgun Kepenk. Tüm hakları saklıdır.</p>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event(OPEN_CONSENT_EVENT))}
            className="mt-3 text-sm text-white/75 underline hover:text-white"
          >
            Çerez tercihlerini değiştir
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 
